/* ======================================================================
   VISIO REBUILD
   Bildkarussell in der Produktkachel.

   Bewusst ohne Custom Element und ohne Bibliothek: ein einziger
   Klick-Listener auf dem Dokument. Dadurch funktionieren auch Kacheln,
   die spaeter nachgeladen werden, etwa beim Filtern oder beim
   unendlichen Scrollen, ohne dass irgendetwas neu initialisiert wird.

   Faellt dieses Skript aus, bleibt schlicht das erste Bild stehen.
   Die Kachel bleibt vollstaendig klickbar.

   Stand: 7. August 2026
   ====================================================================== */

(() => {
  'use strict';

  const STEP_ATTR = 'data-vs-arrow';

  function figureOf(element) {
    return element.closest('.product-card__figure');
  }

  function paint(figure) {
    const track = figure.querySelector('[data-vs-track]');
    if (!track) return;

    const total = track.children.length;
    const index = Math.min(Math.max(parseInt(figure.dataset.vsIndex || '0', 10), 0), total - 1);
    figure.dataset.vsIndex = String(index);

    track.style.transform = `translateX(${index * -100}%)`;

    const counter = figure.querySelector('[data-vs-counter]');
    if (counter) counter.textContent = `${index + 1}/${total}`;

    const prev = figure.querySelector('[' + STEP_ATTR + '="-1"]');
    const next = figure.querySelector('[' + STEP_ATTR + '="1"]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === total - 1;
  }

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[' + STEP_ATTR + ']');
    if (!arrow) return;

    const figure = figureOf(arrow);
    if (!figure) return;

    /* Der Pfeil liegt optisch ueber dem Produktlink.
       Ohne das hier wuerde der Klick zusaetzlich die Produktseite oeffnen. */
    event.preventDefault();
    event.stopPropagation();

    const step = parseInt(arrow.getAttribute(STEP_ATTR), 10);
    const track = figure.querySelector('[data-vs-track]');

    /* Auf Touchgeraeten ist die Spur ein Wisch-Slider. Dort wird
       gescrollt statt verschoben, sonst kaempfen Pfeil und Wischen
       gegeneinander. */
    if (track && track.scrollWidth > track.clientWidth + 4) {
      track.scrollBy({ left: step * track.clientWidth, behavior: 'smooth' });
      return;
    }

    const current = parseInt(figure.dataset.vsIndex || '0', 10);
    figure.dataset.vsIndex = String(current + step);
    paint(figure);
  });

  /* Auf Touchgeraeten uebernimmt scroll-snap. Wir spiegeln nur den Zaehler. */
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!track.hasAttribute || !track.hasAttribute('data-vs-track')) return;

      const figure = figureOf(track);
      if (!figure) return;

      const counter = figure.querySelector('[data-vs-counter]');
      if (!counter) return;

      const width = track.clientWidth || 1;
      const index = Math.round(track.scrollLeft / width);
      counter.textContent = `${Math.min(track.children.length, index + 1)}/${track.children.length}`;
    },
    true
  );
})();


/* ======================================================================
   HERVORGEHOBENE MENUePUNKTE

   Markiert Menueeintraege, deren Beschriftung in den Theme-Einstellungen
   unter "Hervorgehobene Menuepunkte" hinterlegt ist. Konfiguration kommt
   aus window.visioHighlight, das layout/theme.liquid ausgibt.

   Bewusst ueber den Text statt ueber eine URL: so trifft es denselben
   Eintrag im Hauptmenue, im Mega-Menue, in der Quick-Navigation und im
   mobilen Menue, ohne dass an vier Stellen etwas gepflegt werden muss.
   ====================================================================== */
(() => {
  'use strict';

  const config = window.visioHighlight;
  if (!config || !Array.isArray(config.labels)) return;

  const wanted = config.labels
    .map((label) => String(label).trim().toLowerCase())
    .filter(Boolean);

  if (!wanted.length) return;

  const SELECTOR = [
    '.header__link-list a',
    '.header__primary-nav a',
    '.header__primary-nav summary',
    '.mega-menu a',
    '.sticky-subnav__link',
    '.header-sidebar__linklist-button',
    '.header-sidebar__headline'
  ].join(', ');

  function mark(root) {
    root.querySelectorAll(SELECTOR).forEach((element) => {
      if (element.dataset.vsHl) return;

      const label = (element.textContent || '').trim().toLowerCase();
      if (!wanted.includes(label)) return;

      element.dataset.vsHl = config.style || 'outline';
    });
  }

  const run = () => mark(document);
  run();

  /* Menues werden teils erst beim Oeffnen in den DOM gehaengt. */
  new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
})();


/* ======================================================================
   QUICK-NAV FOLGT DEM HEADER

   Die Leiste soll immer direkt unter dem Header haengen, egal wie hoch
   er gerade ist. Der Header schrumpft beim Scrollen, weil die
   Ankuendigungsleiste darueber wegscrollt.

   Frueher haing das an einem Scroll-Listener. Der feuerte nicht in allen
   Faellen, dadurch blieb die Leiste auf dem Startwert stehen. Jetzt
   laeuft eine dauerhafte Schleife ueber requestAnimationFrame und
   schreibt nur, wenn sich der Wert tatsaechlich geaendert hat. Das ist
   unabhaengig davon, welches Element scrollt.
   ====================================================================== */
(() => {
  'use strict';

  const subnav = document.querySelector('.sticky-subnav');
  if (!subnav) return;

  /* Wichtig: das innere .header-Element messen, nicht die Section.
     Prestige blendet den Header mit transform: translateY(-100%) auf
     .header aus. Die Section bleibt dabei stehen. Wer die Section misst,
     bekommt einen Wert, der sich beim Ausblenden nicht aendert. */
  const header =
    document.querySelector('.header') ||
    document.querySelector('.shopify-section--header');

  if (!header) return;

  subnav.dataset.vsFollows = 'header';

  let last = null;
  let reserved = null;

  const overlay = !!document.querySelector(
    '.shopify-section:first-child [allow-transparent-header]'
  );

  if (overlay) subnav.dataset.vsOverlay = 'true';

  function tick() {
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));

    if (bottom !== last) {
      last = bottom;
      subnav.style.setProperty('--vs-subnav-top', bottom + 'px');
    }

    /* Die Leiste schwebt. Ihre Section muss deshalb genau so viel Platz
       reservieren, wie die Leiste hoch ist, sonst rutscht der
       Kollektionsname darunter und wird verdeckt. */
    const own = Math.round(subnav.getBoundingClientRect().height);
    if (own && own !== reserved) {
      reserved = own;
      /* Wichtig: Platz als Innenabstand oben reservieren, nicht als feste
         Hoehe. In derselben Section steht auch die Filterzeile. Eine feste
         Hoehe wuerde sie aus der Section herausdruecken, und sie landet
         dann ueber der Brotkrume. Genau das war der Ueberlappungsfehler. */
      const holder = subnav.closest('.shopify-section');
      if (holder) {
        holder.style.height = '';

        /* Liegt auf der Seite ein Hero mit transparentem Header, darf die
           Leiste keinen Platz reservieren. Sonst schiebt sie das Bild nach
           unten und der Header ist nicht mehr transparent darueber.
           Dort schwebt sie stattdessen ueber dem Bild. */
        holder.style.paddingTop = overlay ? '0px' : own + 'px';
      }
      document.documentElement.style.setProperty('--vs-subnav-height', own + 'px');
    }

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
})();


/* ======================================================================
   ZIEHEN MIT DER MAUS IN DER PRODUKTKACHEL

   Auf dem Handy blaettert man die Kachel durch Wischen. Am Rechner ging
   das bisher nur ueber die Pfeile. Hier kommt dieselbe Geste dazu:
   linke Maustaste halten und ziehen.

   Der Kniff liegt im Klick danach. Die Kachel ist ein Link, ein Ziehen
   wuerde sonst die Produktseite oeffnen. Deshalb merken wir uns die
   zurueckgelegte Strecke und unterdruecken den Klick, sobald mehr als
   ein paar Pixel gezogen wurde. Ein normaler Klick bleibt ein Klick.

   Trackpads scrollen die Spur ohnehin waagerecht, das kommt gratis dazu,
   weil die Spur ein echter Scroll-Container ist.
   ====================================================================== */
(() => {
  'use strict';

  const DRAG_THRESHOLD = 6; /* Pixel, ab denen es als Ziehen gilt */

  let track = null;
  let startX = 0;
  let startScroll = 0;
  let moved = 0;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const candidate = event.target.closest('[data-vs-track]');
    if (!candidate) return;
    if (candidate.scrollWidth <= candidate.clientWidth + 4) return;

    track = candidate;
    startX = event.clientX;
    startScroll = track.scrollLeft;
    moved = 0;
    track.style.scrollSnapType = 'none';
    track.style.cursor = 'grabbing';
  });

  document.addEventListener('pointermove', (event) => {
    if (!track) return;

    const delta = event.clientX - startX;
    moved = Math.max(moved, Math.abs(delta));
    track.scrollLeft = startScroll - delta;

    if (moved > DRAG_THRESHOLD) event.preventDefault();
  });

  function release() {
    if (!track) return;

    track.style.scrollSnapType = '';
    track.style.cursor = '';

    /* Nach dem Loslassen auf das naechste Bild einrasten. */
    const width = track.clientWidth || 1;
    track.scrollTo({ left: Math.round(track.scrollLeft / width) * width, behavior: 'smooth' });

    if (moved > DRAG_THRESHOLD) {
      const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
      document.addEventListener('click', swallow, { capture: true, once: true });
    }

    track = null;
  }

  document.addEventListener('pointerup', release);
  document.addEventListener('pointercancel', release);

  /* Bilder sind von Haus aus ziehbar, das wuerde die Geste stoeren. */
  document.addEventListener('dragstart', (event) => {
    if (event.target.closest('[data-vs-track]')) event.preventDefault();
  });
})();

/* ======================================================================
   FOOTER: AUF DESKTOP SPALTEN STATT REITER

   Die Footer-Section "Tabs" liefert zwei Darstellungen mit demselben
   Inhalt: eine Reiterleiste fuer breite Bildschirme und eine
   Ausklappliste fuer schmale. Auf Desktop zeigt die Reiterleiste immer
   nur eine Rubrik. Gewuenscht sind vier offene Spalten.

   Dieses Skript blendet ab 700px die Ausklappliste ein, oeffnet ihre
   Eintraege und setzt die Klasse vs-cols. Erst diese Klasse aktiviert
   Abschnitt 42 der CSS. Faellt das Skript aus, greift keine einzige
   Regel und die Reiterleiste bleibt wie bisher stehen. Der Footer kann
   dadurch nicht leer werden.

   Das echte Oeffnen ist noetig, weil ein geschlossenes details-Element
   seinen Inhalt nicht in die eigene Hoehe aufnimmt. Nur sichtbar machen
   reicht nicht, der Text liefe unten heraus.

   Unter 700px wird alles zurueckgebaut. Geschlossen werden dabei nur
   Eintraege, die dieses Skript selbst geoeffnet hat. Was der Besucher
   von Hand geoeffnet hat, bleibt offen.
   ====================================================================== */
(() => {
  'use strict';

  const ABFRAGE = window.matchMedia('(min-width: 700px)');

  function anwenden() {
    document.querySelectorAll('.shopify-section--tabs').forEach((section) => {
      /* Nur der Footer. Dieselbe Section kann auch im Seiteninhalt stehen,
         dort waere ein Spaltenumbau falsch. */
      if (!section.classList.contains('shopify-section-group-footer-group')) return;

      if (ABFRAGE.matches) {
        section.classList.add('vs-cols');
        section.querySelectorAll('details').forEach((eintrag) => {
          if (!eintrag.open) {
            eintrag.open = true;
            eintrag.dataset.vsAuf = '1';
          }
        });
      } else {
        section.classList.remove('vs-cols');
        section.querySelectorAll('details[data-vs-auf]').forEach((eintrag) => {
          eintrag.open = false;
          delete eintrag.dataset.vsAuf;
        });
      }
    });
  }

  anwenden();
  ABFRAGE.addEventListener('change', anwenden);

  /* Der Theme-Editor baut Sections beim Bearbeiten neu auf. */
  document.addEventListener('shopify:section:load', anwenden);
})();